import * as React from "react";
import { Send } from "lucide-react";
import { Field, RadioGroup, Label } from "@/components/ui";
import { SocialNetworksId } from "@/interfaces";
import { VideoProject } from "@/services/bedrock";
import { SocialNetworkIconButton } from "@/components/SocialNetworkIconButton";
import { ProjectScheduler } from "@/components/Forms/VideoProject/components/ProjectScheduler";
import { cn } from "@/lib/utils";
import { EditorVideoProjectField } from "../EditorCaptionSettings";

interface Props {
  videoProject: VideoProject;
  disabled: boolean;
  postableSocialNetworks: string[];
  onChangeField: (field: EditorVideoProjectField, value: any) => void;
}

export const EditorPostingPreferences: React.FC<Props> = ({
  videoProject,
  disabled,
  postableSocialNetworks,
  onChangeField,
}) => {
  const isAutoPost = !!videoProject.isAutoPost;
  const selectedSocialNetworkIds = videoProject.post?.postSocialNetworks?.map(sn => sn.socialNetworkId) ?? [];
  const methodLabelClassName = cn("font-semibold", disabled ? "cursor-default" : "cursor-pointer");

  return (
    <Field className="flex flex-col gap-1.5">
      <Field.Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
        <Send className="size-3.5 text-slate-400" />
        Publishing Method
      </Field.Label>
      <Field.Content className="mt-2">
        <RadioGroup
          value={isAutoPost ? "autoPost" : "pushNotification"}
          disabled={disabled}
          onValueChange={val => {
            onChangeField("isAutoPost", val === "autoPost");
          }}
          className="flex flex-col gap-5 pl-4"
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center space-x-2">
              <RadioGroup.Item value="autoPost" id="autoPost" disabled={disabled} />
              <Label htmlFor="autoPost" className={cn("text-slate-800 font-sans", methodLabelClassName)}>
                Auto Post
              </Label>
            </div>

            {isAutoPost && (
              <div className="pl-6 flex flex-col gap-4 mt-1">
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Select Social Networks
                  </span>
                  <div className="flex flex-row flex-wrap items-center gap-2.5">
                    {postableSocialNetworks.map((networkId, index) => {
                      return (
                        <SocialNetworkIconButton
                          disabled={disabled}
                          size="noMargin"
                          key={"video_project_post_sn_icon_" + index}
                          active={selectedSocialNetworkIds.includes(networkId as SocialNetworksId)}
                          networkId={networkId as SocialNetworksId}
                          customIconClassname="socialNetworkIconCentered"
                          onClickHandler={checked => {
                            let snUpdated: SocialNetworksId[] = [...selectedSocialNetworkIds];
                            if (checked) {
                              snUpdated = snUpdated.concat([networkId as SocialNetworksId]);
                            } else {
                              snUpdated = snUpdated.filter((id: string) => id !== networkId);
                            }
                            onChangeField("socialNetworkIds", snUpdated);
                          }}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Schedule Time</span>
                  <div className="w-full bg-white border border-slate-200 p-4 rounded-xl shadow-xs">
                    <ProjectScheduler
                      deliveryDate={videoProject.preferredPostDate || new Date().toISOString()}
                      editable={!disabled}
                      minDate={new Date()}
                      onChange={(date: Date) => {
                        onChangeField("preferredPostDate", date.toISOString());
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <RadioGroup.Item value="pushNotification" id="pushNotification" disabled={disabled} />
            <Label htmlFor="pushNotification" className={cn("text-slate-900", methodLabelClassName)}>
              Push Notification
            </Label>
          </div>
        </RadioGroup>
      </Field.Content>
    </Field>
  );
};
